import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useBillingReconciliationVarianceActions,
  useCreateBillingReconciliationVarianceAction,
  useDeleteBillingReconciliationVarianceAction,
  useResolveBillingReconciliationVarianceActions,
  useResetBillingReconciliationVarianceActions,
  useSaveBillingReconciliationVarianceActions,
  useUpdateBillingReconciliationVarianceAction,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { VARIANCE_ACTIONS } from './varianceActions.js';
import { listActionsFromPayload } from './varianceDrawerModel.js';
import { forceEvidenceKey } from './forcedEvidence.js';
import { varianceDropActionKey } from './evidenceDropAction.js';
import { varianceActionMathKey } from './varianceActionTotals.js';

const actionVersionSnapshot = (actions = [], statuses = null) => {
  const allowed = Array.isArray(statuses) && statuses.length ? new Set(statuses) : null;
  return actions
    .filter((action) => action?.id && action.updatedAt)
    .filter((action) => {
      if (!allowed) return true;
      return allowed.has(action.saveStatus || action.status || 'draft');
    })
    .map((action) => ({ id: action.id, updatedAt: action.updatedAt }));
};
const actionSignature = (actions = []) =>
  actions.map((action) => `${action.id || ''}:${action.updatedAt || action.status || action.saveStatus || ''}`).join('|');

export const useVarianceDrawerActions = ({
  row,
  runId,
  open,
  mockMode,
  currentUserName,
  onActionsChangedAfterCompletion = null,
  prefetchedActions = null,
}) => {
  const queryClient = useQueryClient();
  const [varianceActions, setVarianceActions] = useState([]);
  const [varianceActionError, setVarianceActionError] = useState('');
  const forceEvidenceKeysRef = useRef(new Set());
  const dropActionKeysRef = useRef(new Set());
  const lastAppliedPrefetchedActionsSignatureRef = useRef(null);
  const pendingActionWritesRef = useRef(new Set());
  const varianceActionsRef = useRef([]);
  const actionGenerationRef = useRef(0);
  const [pendingActionWriteCount, setPendingActionWriteCount] = useState(0);
  const prefetchedActionsSignature = Array.isArray(prefetchedActions) ? actionSignature(prefetchedActions) : '';
  const hasPrefetchedActionsPayload = Array.isArray(prefetchedActions);

  const varianceActionsQuery = useBillingReconciliationVarianceActions(
    { varianceId: row?.id },
    { enabled: open && !mockMode && Boolean(row?.id) && !hasPrefetchedActionsPayload },
  );
  const createVarianceAction = useCreateBillingReconciliationVarianceAction();
  const updateVarianceAction = useUpdateBillingReconciliationVarianceAction();
  const deleteVarianceAction = useDeleteBillingReconciliationVarianceAction();
  const saveVarianceActions = useSaveBillingReconciliationVarianceActions();
  const resolveVarianceActions = useResolveBillingReconciliationVarianceActions();
  const resetVarianceActions = useResetBillingReconciliationVarianceActions();

  const updateVarianceActions = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(varianceActionsRef.current) : updater;
    varianceActionsRef.current = next;
    setVarianceActions(next);
  }, []);
  const cacheVarianceActions = useCallback((nextActions) => {
    if (!row?.id) return;
    queryClient.setQueryData(['billingRecon', 'varianceActions', row.id], nextActions);
    if (!runId || !row?.bac) return;
    queryClient.setQueriesData({ queryKey: ['billingRecon', 'bacContext', runId, row.bac] }, (current) => (
      current && typeof current === 'object'
        ? { ...current, workflow: { ...(current.workflow || {}), actions: nextActions } }
        : current
    ));
    queryClient.setQueriesData({ queryKey: ['billingRecon', 'operatorContext', runId, row.bac] }, (current) => {
      if (!current || typeof current !== 'object') return current;
      return {
        ...current,
        workflow: { ...(current.workflow || {}), actions: nextActions },
        operatorBundle: current.operatorBundle
          ? {
            ...current.operatorBundle,
            varianceActions: {
              ...(current.operatorBundle.varianceActions || {}),
              status: 'ready',
              data: nextActions,
            },
          }
          : current.operatorBundle,
      };
    });
  }, [queryClient, row?.bac, row?.id, runId]);

  useEffect(() => {
    updateVarianceActions([]);
    setVarianceActionError('');
    forceEvidenceKeysRef.current.clear();
    dropActionKeysRef.current.clear();
    lastAppliedPrefetchedActionsSignatureRef.current = null;
    actionGenerationRef.current += 1;
    pendingActionWritesRef.current.clear();
    setPendingActionWriteCount(0);
  }, [open, row?.id, runId, updateVarianceActions]);

  useEffect(() => {
    varianceActionsRef.current = varianceActions;
  }, [varianceActions]);

  useEffect(() => {
    if (!open) return;
    if (hasPrefetchedActionsPayload) {
      updateVarianceActions((current) => {
        const currentSignature = actionSignature(current);
        if (
          lastAppliedPrefetchedActionsSignatureRef.current === prefetchedActionsSignature
          && currentSignature !== prefetchedActionsSignature
        ) {
          return current;
        }
        lastAppliedPrefetchedActionsSignatureRef.current = prefetchedActionsSignature;
        return currentSignature === prefetchedActionsSignature ? current : prefetchedActions;
      });
      return;
    }
    if (mockMode || !varianceActionsQuery.isSuccess) return;
    updateVarianceActions(Array.isArray(varianceActionsQuery.data) ? varianceActionsQuery.data : []);
  }, [hasPrefetchedActionsPayload, mockMode, open, prefetchedActions, prefetchedActionsSignature, updateVarianceActions, varianceActionsQuery.isSuccess, varianceActionsQuery.data]);

  useEffect(() => {
    forceEvidenceKeysRef.current = new Set(
      varianceActions
        .filter((action) => action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH)
        .map(forceEvidenceKey),
    );
    dropActionKeysRef.current = new Set(varianceActions.map(varianceDropActionKey));
  }, [varianceActions]);

  const isCurrentActionGeneration = useCallback((generation) => generation === actionGenerationRef.current, []);

  const trackActionWrite = useCallback((promise) => {
    const generation = actionGenerationRef.current;
    pendingActionWritesRef.current.add(promise);
    setPendingActionWriteCount((count) => count + 1);
    const cleanup = () => {
      pendingActionWritesRef.current.delete(promise);
      if (isCurrentActionGeneration(generation)) {
        setPendingActionWriteCount((count) => Math.max(0, count - 1));
      }
    };
    promise.then(cleanup, cleanup);
    return promise;
  }, [isCurrentActionGeneration]);

  const waitForPendingActionWrites = useCallback(async () => {
    const pending = [...pendingActionWritesRef.current];
    if (!pending.length) return;
    await Promise.allSettled(pending);
  }, []);

  const persistVarianceAction = useCallback(async (action) => {
    onActionsChangedAfterCompletion?.();
    const draftAction = { ...action, saveStatus: action.saveStatus || 'draft' };
    const mathKey = varianceActionMathKey(draftAction);
    const existingAction = mathKey
      ? varianceActions.find((existing) => varianceActionMathKey(existing) === mathKey)
      : null;
    setVarianceActionError('');
    if (existingAction) {
      const mergedAction = {
        ...existingAction,
        ...draftAction,
        id: existingAction.id,
        createdAt: existingAction.createdAt,
        createdBy: existingAction.createdBy,
        saveStatus: 'draft',
      };
      updateVarianceActions((current) => current.map((existing) => (existing.id === existingAction.id ? mergedAction : existing)));
      if (mockMode || !row?.id) return mergedAction;
      const generation = actionGenerationRef.current;
      try {
        const saved = await trackActionWrite((async () => {
          const nextSaved = await updateVarianceAction.mutateAsync({
            id: existingAction.id,
            runId,
            varianceId: row.id,
            ifUpdatedAt: existingAction.updatedAt,
            action: mergedAction,
            patch: {
              ...mergedAction,
              status: mergedAction.saveStatus,
            },
          });
          if (!isCurrentActionGeneration(generation)) return nextSaved;
          updateVarianceActions((current) => {
            const next = current.map((existing) => (existing.id === existingAction.id ? nextSaved : existing));
            cacheVarianceActions(next);
            return next;
          });
          return nextSaved;
        })());
        return saved;
      } catch (error) {
        if (isCurrentActionGeneration(generation)) {
          updateVarianceActions((current) => current.map((existing) => (existing.id === existingAction.id ? existingAction : existing)));
          setVarianceActionError(error?.message || 'Could not update the variance action.');
        }
        throw error;
      }
    }
    updateVarianceActions((current) => [draftAction, ...current]);
    if (mockMode || !row?.id) return draftAction;
    const generation = actionGenerationRef.current;
    try {
      const saved = await trackActionWrite((async () => {
        const nextSaved = await createVarianceAction.mutateAsync({
          runId,
          varianceId: row.id,
          action: {
            ...draftAction,
            status: draftAction.saveStatus,
          },
        });
        if (!isCurrentActionGeneration(generation)) return nextSaved;
        updateVarianceActions((current) => {
          const next = current.map((existing) => (existing.id === draftAction.id ? nextSaved : existing));
          cacheVarianceActions(next);
          return next;
        });
        return nextSaved;
      })());
      return saved;
    } catch (error) {
      if (isCurrentActionGeneration(generation)) {
        updateVarianceActions((current) => current.filter((existing) => existing.id !== draftAction.id));
        setVarianceActionError(error?.message || 'Could not save the variance action.');
      }
      throw error;
    }
  }, [cacheVarianceActions, createVarianceAction, isCurrentActionGeneration, mockMode, onActionsChangedAfterCompletion, row?.id, runId, trackActionWrite, updateVarianceAction, updateVarianceActions, varianceActions]);

  const noteDropKey = useCallback((action) => {
    const isForceEvidence = action.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH;
    return {
      isForceEvidence,
      key: isForceEvidence ? forceEvidenceKey(action) : varianceDropActionKey(action),
    };
  }, []);

  const shouldAcceptDropAction = useCallback((action, { reserve = true } = {}) => {
    const { isForceEvidence, key } = noteDropKey(action);
    if (isForceEvidence && forceEvidenceKeysRef.current.has(key)) return false;
    if (!isForceEvidence && dropActionKeysRef.current.has(key)) return false;
    if (reserve) {
      if (isForceEvidence) forceEvidenceKeysRef.current.add(key);
      else dropActionKeysRef.current.add(key);
    }
    return true;
  }, [noteDropKey]);

  const rollbackDropAction = useCallback((action) => {
    const { isForceEvidence, key } = noteDropKey(action);
    if (isForceEvidence) forceEvidenceKeysRef.current.delete(key);
    else dropActionKeysRef.current.delete(key);
  }, [noteDropKey]);

  const removeVarianceAction = useCallback(async (actionId) => {
    onActionsChangedAfterCompletion?.();
    const existing = varianceActions.find((action) => action.id === actionId);
    const previous = varianceActions;
    setVarianceActionError('');
    if (existing?.actionType === VARIANCE_ACTIONS.FORCE_EVIDENCE_MATCH) {
      forceEvidenceKeysRef.current.delete(forceEvidenceKey(existing));
    }
    if (existing) dropActionKeysRef.current.delete(varianceDropActionKey(existing));
    updateVarianceActions((current) => {
      const next = current.filter((action) => action.id !== actionId);
      cacheVarianceActions(next);
      return next;
    });
    if (!mockMode && row?.id && existing) {
      const generation = actionGenerationRef.current;
      try {
        await trackActionWrite(deleteVarianceAction.mutateAsync({
          id: actionId,
          runId,
          varianceId: row.id,
          ifUpdatedAt: existing.updatedAt,
        }));
      } catch (error) {
        if (isCurrentActionGeneration(generation)) {
          updateVarianceActions(previous);
          cacheVarianceActions(previous);
          setVarianceActionError(error?.message || 'Could not remove the variance action.');
        }
      }
    }
  }, [cacheVarianceActions, deleteVarianceAction, isCurrentActionGeneration, mockMode, onActionsChangedAfterCompletion, row?.id, runId, trackActionWrite, updateVarianceActions, varianceActions]);

  const patchVarianceActionFields = useCallback(async (actionId, patch = {}) => {
    onActionsChangedAfterCompletion?.();
    const existing = varianceActions.find((action) => action.id === actionId);
    if (!existing) {
      if (mockMode || !row?.id) return null;
      const error = new Error('Could not update the variance action because its current version is not loaded. Refresh and try again.');
      setVarianceActionError(error.message);
      throw error;
    }
    const previous = varianceActions;
    const nextStatus = patch.status || existing.status || 'draft';
    const mergedAction = {
      ...existing,
      ...patch,
      saveStatus: nextStatus,
      status: nextStatus,
    };
    setVarianceActionError('');
    updateVarianceActions((current) => current.map((action) => (action.id === actionId ? mergedAction : action)));
    if (mockMode || !row?.id) return mergedAction;
    const generation = actionGenerationRef.current;
    try {
      const saved = await trackActionWrite((async () => {
        const nextSaved = await updateVarianceAction.mutateAsync({
          id: actionId,
          runId,
          varianceId: row.id,
          ifUpdatedAt: existing.updatedAt,
          action: mergedAction,
          patch: { ...patch, status: nextStatus },
        });
        if (!isCurrentActionGeneration(generation)) return nextSaved;
        updateVarianceActions((current) => {
          const next = current.map((action) => (action.id === actionId ? nextSaved : action));
          cacheVarianceActions(next);
          return next;
        });
        return nextSaved;
      })());
      return saved;
    } catch (error) {
      if (isCurrentActionGeneration(generation)) {
        updateVarianceActions(previous);
        cacheVarianceActions(previous);
        setVarianceActionError(error?.message || 'Could not update the variance action.');
      }
      throw error;
    }
  }, [cacheVarianceActions, isCurrentActionGeneration, mockMode, onActionsChangedAfterCompletion, row?.id, runId, trackActionWrite, updateVarianceAction, updateVarianceActions, varianceActions]);

  const resetAllVarianceActions = useCallback(async () => {
    onActionsChangedAfterCompletion?.();
    await waitForPendingActionWrites();
    const previous = varianceActionsRef.current;
    setVarianceActionError('');
    forceEvidenceKeysRef.current.clear();
    dropActionKeysRef.current.clear();
    updateVarianceActions([]);
    cacheVarianceActions([]);
    if (!mockMode && row?.id) {
      try {
        await resetVarianceActions.mutateAsync({
          runId,
          varianceId: row.id,
          expectedActionVersions: actionVersionSnapshot(previous, ['draft', 'saved', 'resolved']),
        });
      } catch (error) {
        updateVarianceActions(previous);
        cacheVarianceActions(previous);
        setVarianceActionError(error?.message || 'Could not reset variance actions.');
      }
    }
  }, [cacheVarianceActions, mockMode, onActionsChangedAfterCompletion, resetVarianceActions, row?.id, runId, updateVarianceActions, waitForPendingActionWrites]);

  const saveAllVarianceActions = useCallback(async () => {
    await waitForPendingActionWrites();
    const now = new Date().toISOString();
    const previous = varianceActionsRef.current;
    setVarianceActionError('');
    const optimisticSavedActions = varianceActionsRef.current.map((action) => ({
      ...action,
      saveStatus: 'saved',
      savedAt: action.savedAt || now,
      savedBy: currentUserName,
    }));
    updateVarianceActions(optimisticSavedActions);
    cacheVarianceActions(optimisticSavedActions);
    if (!mockMode && row?.id) {
      try {
        const saved = await saveVarianceActions.mutateAsync({
          runId,
          varianceId: row.id,
          expectedActionVersions: actionVersionSnapshot(previous, ['draft']),
        });
        const savedActions = listActionsFromPayload(saved);
        updateVarianceActions(savedActions);
        cacheVarianceActions(savedActions);
      } catch (error) {
        updateVarianceActions(previous);
        cacheVarianceActions(previous);
        setVarianceActionError(error?.message || 'Could not save variance actions.');
      }
    }
  }, [cacheVarianceActions, currentUserName, mockMode, row?.id, runId, saveVarianceActions, updateVarianceActions, waitForPendingActionWrites]);

  const resolveAllVarianceActions = useCallback(async () => {
    const eligible = varianceActions.filter((action) => ['saved', 'exported'].includes(action.saveStatus || action.status));
    if (!eligible.length) return varianceActions;
    const previous = varianceActions;
    setVarianceActionError('');
    updateVarianceActions((current) => current.map((action) => (
      eligible.some((candidate) => candidate.id === action.id)
        ? { ...action, saveStatus: 'resolved', status: 'resolved' }
        : action
    )));
    if (mockMode || !row?.id) return eligible;
    try {
      const resolved = await resolveVarianceActions.mutateAsync({ runId, varianceId: row.id });
      const resolvedActions = listActionsFromPayload(resolved);
      updateVarianceActions(resolvedActions);
      cacheVarianceActions(resolvedActions);
      return resolved;
    } catch (error) {
      updateVarianceActions(previous);
      cacheVarianceActions(previous);
      setVarianceActionError(error?.message || 'Could not mark variance actions resolved.');
      throw error;
    }
  }, [cacheVarianceActions, mockMode, resolveVarianceActions, row?.id, runId, updateVarianceActions, varianceActions]);

  return {
    varianceActions,
    varianceActionError,
    setVarianceActionError,
    persistVarianceAction,
    patchVarianceActionFields,
    removeVarianceAction,
    resetAllVarianceActions,
    resolveAllVarianceActions,
    saveAllVarianceActions,
    shouldAcceptDropAction,
    rollbackDropAction,
    isVarianceActionSaveBusy: saveVarianceActions.isPending,
    isVarianceActionBusy: saveVarianceActions.isPending
      || resolveVarianceActions.isPending
      || resetVarianceActions.isPending
      || pendingActionWriteCount > 0,
  };
};
